import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Cell,
} from "recharts";
import { getEstadoInteresBadgeClass, getEstadoInteresLabel } from "../../../../utils/estadoInteres";
import OrigenTendenciaChart from "../../../dashboard/components/OrigenTendenciaChart";

const formatNumber = (value) => {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString("es-EC");
};

const accentMap = {
  blue: { bg: "#e0ecff", fg: "#1d4ed8" },
  violet: { bg: "#ebe9ff", fg: "#6d28d9" },
  green: { bg: "#e5f8f2", fg: "#0f766e" },
  amber: { bg: "#fff4e0", fg: "#c2410c" },
  aqua: { bg: "#e0f7ff", fg: "#0ea5e9" },
  coral: { bg: "#ffe7e2", fg: "#dc2626" },
};

const stageOrder = ["Interesados", "Inscritos"];

const buildDefaultFilters = () => ({
  funnelStages: [...stageOrder],
  funnelMonth: "all",
  funnelYear: "all",
  visitsStatus: "all",
  visitsTransfer: "all",
  visitsSearch: "",
});

const monthLabels = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const toMonthLabel = (value) => {
  const idx = Number(value) - 1;
  return monthLabels[idx] || value;
};

export default function OperativoTab({ operativo = {} }) {
  const kpis = operativo.kpis || [];
  const rawFunnel = operativo.funnel || [];
  const funnelMonthly = operativo.funnelMonthly || [];
  const visitas = operativo.visitas || [];
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters);
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = (section) => {
    setDraftFilters(filters);
    setOpenDropdown((prev) => (prev === section ? null : section));
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    closeDropdown();
  };

  const resetDraftFilters = () => {
    setDraftFilters(buildDefaultFilters());
  };

  const funnelPeriod =
    filters.funnelYear !== "all" && filters.funnelMonth !== "all"
      ? `${filters.funnelYear}-${filters.funnelMonth}`
      : null;
  const funnelSource = funnelPeriod
    ? funnelMonthly.filter((row) => row.period === funnelPeriod)
    : rawFunnel;
  const funnelOrdered = stageOrder.map((stage) => {
    const hit =
      funnelSource.find((f) => (f.stage || f.etapa || "").toLowerCase() === stage.toLowerCase()) || {
        stage,
        value: 0,
      };
    return { stage, value: Number(hit.value) || 0 };
  });

  const funnelData = funnelOrdered.map((item, idx) => {
    const prev = idx > 0 ? funnelOrdered[idx - 1].value : null;
    const pctPrev = prev ? (item.value / prev) * 100 : 100;
    return {
      ...item,
      pctPrev: Number.isFinite(pctPrev) ? pctPrev : 0,
    };
  });

  const activeFunnelStages = filters.funnelStages.length ? filters.funnelStages : stageOrder;
  const filteredFunnelData = funnelData.filter((item) => activeFunnelStages.includes(item.stage));
  const funnelForChart = filteredFunnelData.length ? filteredFunnelData : funnelData;
  const funnelForChartAdjusted = funnelForChart.map((item, idx, arr) => {
    const prev = idx > 0 ? arr[idx - 1].value : null;
    const pctPrev = prev ? (item.value / prev) * 100 : 100;
    return {
      ...item,
      pctPrev: Number.isFinite(pctPrev) ? pctPrev : 0,
    };
  });
  const maxValue = Math.max(1, ...funnelForChartAdjusted.map((d) => d.value));
  const funnelColors = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#14b8a6"];
  const searchTerm = (filters.visitsSearch || "").trim().toLowerCase();
  const filteredVisitas = visitas.filter((v) => {
    const statusMatch = filters.visitsStatus === "all" || v.estado === filters.visitsStatus;
    const transferMatch =
      filters.visitsTransfer === "all" ||
      (filters.visitsTransfer === "si" ? Boolean(v.transferido) : !v.transferido);
    const searchMatch =
      !searchTerm ||
      [v.visitante, v.novedad, v.estado_interes, v.recepcionista_nombre, v.recepcionista_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchTerm));
    return statusMatch && transferMatch && searchMatch;
  });

  const funnelYears = Array.from(
    new Set(
      funnelMonthly
        .map((row) => (row.period ? row.period.slice(0, 4) : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b.localeCompare(a));
  const funnelMonths = Array.from(
    new Set(
      funnelMonthly
        .filter((row) =>
          draftFilters.funnelYear === "all" ? Boolean(row.period) : row.period?.startsWith(`${draftFilters.funnelYear}-`)
        )
        .map((row) => (row.period ? row.period.slice(5, 7) : null))
        .filter(Boolean)
    )
  ).sort();


  return (
    <>
      <div className="row g-3 admin-kpi-row">
        {kpis.map((kpi) => (
          <div className="col-12 col-sm-6 col-lg-3" key={kpi.label}>
            <div className="admin-kpi-card h-100">
              <div className="admin-kpi-body">
                <span
                  className="admin-kpi-icon"
                  style={{
                    backgroundColor: accentMap[kpi.accent]?.bg || "#e5e7eb",
                    color: accentMap[kpi.accent]?.fg || "#0f172a",
                  }}
                >
                  <i className={`bi ${kpi.icon || "bi-activity"} me-0`} />
                </span>
                <div className="admin-kpi-text">
                  <p className="admin-kpi-label mb-0">{kpi.label}</p>
                  <div className="admin-kpi-value">{formatNumber(kpi.value)}</div>
                  <div className="admin-kpi-helper">{kpi.helper}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-2">
        <div className="col-12 col-xl-6">
          <div className="card admin-section-card h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Flujo de Interesados</h6>
                <small className="text-muted">Interesados ➡ Inscritos</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="admin-filter-menu">
                  <button
                    type="button"
                    className="btn btn-filter-chip"
                    onClick={() => toggleDropdown("funnel")}
                    aria-expanded={openDropdown === "funnel"}
                  >
                    <i className="bi bi-sliders2 me-1" />
                    Filtros
                    <i className="bi bi-chevron-down ms-1" />
                  </button>
                  {openDropdown === "funnel" && (
                    <div className="admin-filter-panel">
                      <div className="admin-filter-header">
                        <div>
                          <p className="admin-filter-title">Flujo de interesados</p>
                          <small className="text-muted">Filtra por mes y año</small>
                        </div>
                        <button type="button" className="btn btn-light btn-sm" onClick={closeDropdown}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      <div className="row g-3">
                        <div className="col-6">
                          <label className="form-label">Año</label>
                          <select
                            className="form-select"
                            value={draftFilters.funnelYear}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                funnelYear: e.target.value,
                                funnelMonth: e.target.value === "all" ? "all" : prev.funnelMonth,
                              }))
                            }
                          >
                            <option value="all">Todos</option>
                            {funnelYears.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Mes</label>
                          <select
                            className="form-select"
                            value={draftFilters.funnelMonth}
                            disabled={draftFilters.funnelYear === "all"}
                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, funnelMonth: e.target.value }))}
                          >
                            <option value="all">Todos</option>
                            {funnelMonths.map((month) => (
                              <option key={month} value={month}>
                                {toMonthLabel(month)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="admin-filter-actions">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetDraftFilters}>
                          Restablecer
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={applyFilters}>
                          Aplicar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <span className="dashboard-chip">
                  <i className="bi bi-funnel me-1" />
                  {funnelForChartAdjusted.length}
                </span>
              </div>
            </div>
            <div className="p-3 pt-0" style={{ minHeight: 320 }}>
              {funnelForChartAdjusted.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    layout="vertical"
                    data={funnelForChartAdjusted}
                    margin={{ top: 10, right: 24, bottom: 0, left: 12 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis type="number" hide domain={[0, maxValue * 1.1]} />
                    <YAxis type="category" dataKey="stage" width={160} />
                    <Tooltip
                      formatter={(val, name, props) => [
                        formatNumber(val),
                        props && props.payload ? props.payload.stage || "" : "",
                      ]}
                      labelFormatter={() => ""}
                    />
                    <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                      <LabelList
                        dataKey="value"
                        position="right"
                        formatter={(val, entry) => {
                          const pct = entry && entry.pctPrev ? `${Math.round(entry.pctPrev)}%` : "";
                          return `${formatNumber(val)}${pct ? ` (${pct})` : ""}`;
                        }}
                      />
                      {funnelForChartAdjusted.map((entry, index) => (
                        <Cell key={`${entry.stage}-${index}`} fill={funnelColors[index % funnelColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex h-100 align-items-center justify-content-center text-muted">
                  Sin datos de funnel
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <OrigenTendenciaChart
            sources={operativo.sources || []}
            sourcesMonthly={operativo.sourcesMonthly || []}
            cardClassName="admin-section-card"
          />
        </div>
      </div>

      <div className="card admin-section-card mt-3">
        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">Visitas</h6>
            <small className="text-muted">Bitacora operativa diaria</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="admin-filter-menu">
              <button
                type="button"
                className="btn btn-filter-chip"
                onClick={() => toggleDropdown("visitas")}
                aria-expanded={openDropdown === "visitas"}
              >
                <i className="bi bi-sliders2 me-1" />
                Filtros
                <i className="bi bi-chevron-down ms-1" />
              </button>
              {openDropdown === "visitas" && (
                <div className="admin-filter-panel">
                  <div className="admin-filter-header">
                    <div>
                      <p className="admin-filter-title">Visitas</p>
                      <small className="text-muted">Filtra por estado o transferido</small>
                    </div>
                    <button type="button" className="btn btn-light btn-sm" onClick={closeDropdown}>
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label">Estado</label>
                      <select
                        className="form-select"
                        value={draftFilters.visitsStatus}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, visitsStatus: e.target.value }))}
                      >
                        <option value="all">Todos</option>
                        <option value="Abierta">Abiertas</option>
                        <option value="Cerrada">Cerradas</option>
                        <option value="Transferida">Transferidas</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Transferido</label>
                      <select
                        className="form-select"
                        value={draftFilters.visitsTransfer}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, visitsTransfer: e.target.value }))}
                      >
                        <option value="all">Todos</option>
                        <option value="si">Solo transferidas</option>
                        <option value="no">No transferidas</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Buscar</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Visitante, novedad, estado o recepcionista"
                        value={draftFilters.visitsSearch}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, visitsSearch: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="admin-filter-actions">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetDraftFilters}>
                      Restablecer
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={applyFilters}>
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
            <span className="dashboard-chip">
              <i className="bi bi-clipboard2-data me-1" />
              {filteredVisitas.length}
            </span>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Visitante</th>
                <th>Estado</th>
                <th>Recepcionista</th>
                <th>Transferido</th>
                <th>Novedad</th>
                <th>Estado interesado</th>
              </tr>
            </thead>
            <tbody>
              {filteredVisitas.length ? (
                filteredVisitas.map((v) => (
                  <tr key={v.id}>
                    <td>{v.visitante}</td>
                    <td>{v.estado}</td>
                    <td>{v.recepcionista_nombre || "-"}</td>
                    <td>{v.transferido ? "Si" : "No"}</td>
                    <td>{v.novedad || "-"}</td>
                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2 ${getEstadoInteresBadgeClass(v.estado_interes)}`}
                      >
                        {getEstadoInteresLabel(v.estado_interes)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    Sin visitas registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
}

